import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { Button, Col, message, Row, Card } from 'antd';
import { yupResolver } from '@hookform/resolvers/yup';
import { Container } from '@/components/Elements';

import useUnsavedChangesWarning from '@/hooks/useUnsavedChangesWarning';
import StepperFormLayout from '@/components/Layout/StepperFormLayout';

import { purchaseFormRoutes } from '../constant/stepperRoutes';
import useScrollToTop from '@/hooks/useScrollTop';
import {
  useGetPurchaseStepDetailQuery,
  usePostPurchaseRequestMutation,
} from '@/pages/mortgage/apis/purchaseAPI';
import { YourPurchaseRequestPayload } from '@/pages/mortgage/types/Purchase';
import { displayError, displaySuccess } from '@/utils/displayMessageUtils';
import StepProgress from '@/components/StepProgress/StepProgress';
import { stepData } from '../constant/stepData';
import { useFetchUserProfileQuery } from '@/store/apis/userApi';
import NewPurchaseDetail from './PurchaseDetail';
import NewPersonalDetail from './PersonalDetail';
import NewPropertyDetail from './PropertyDetail';
import { primaryDetailSchema } from '../schema/primaryDetailSchema';
import makeAllFieldsOptional from '@/utils/yupUtils';
import { ObjectSchema } from 'yup';
import { useAppSelector } from '@/hooks/hooks';

import { PrimaryDetailFormType } from '../types';
import PurchaseRightSidebar from '../purchaseRightSidebar';

const YourPurchaseDetail = () => {
  const { id } = useParams();
  useUnsavedChangesWarning(true);
  useScrollToTop();
  const navigate = useNavigate();
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const [postPurchaseRequest, { isLoading: postPurchaseRequestLoading }] =
    usePostPurchaseRequestMutation();

  const brokerId = useAppSelector((state) => state.broker.brokerId);

  const { data: purchaseDetailApiResponse } = useGetPurchaseStepDetailQuery(
    { application_id: id!, step: 'primary_detail' },
    { skip: !id, refetchOnMountOrArgChange: true },
  );

  const yourPurchaseDetailData = purchaseDetailApiResponse?.data
    .data as PrimaryDetailFormType;

  const primaryDetailSchemaWithOptionalFields =
    makeAllFieldsOptional(primaryDetailSchema);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    unregister,
    register,
    formState: { errors },
    getValues,
    resetField,
  } = useForm<PrimaryDetailFormType>({
    defaultValues: {},
    resolver: yupResolver(
      primaryDetailSchemaWithOptionalFields as ObjectSchema<any>,
    ),
    // shouldUnregister: true,
  });

  useEffect(() => {
    if (yourPurchaseDetailData) {
      setValue('purchasingUnder', yourPurchaseDetailData.purchasingUnder);
      setValue('propertyType', yourPurchaseDetailData.propertyType);
    }
  }, [yourPurchaseDetailData]);

  const buildPayload = useCallback(
    (
      data: PrimaryDetailFormType,
      isDraft = false,
    ): YourPurchaseRequestPayload => {
      const filtered = Object.fromEntries(
        Object.entries(data).filter(
          ([_, v]) =>
            v !== null &&
            v !== undefined &&
            v !== '' &&
            (!Array.isArray(v) || v.length > 0),
        ),
      );
      return {
        data: filtered as PrimaryDetailFormType,
        step: 'primary_detail',
        ...(id && { application_id: id }),
        ...(brokerId ? { broker_id: brokerId } : {}),
        ...(isDraft && { draft: true }),
      };
    },
    [id, brokerId],
  );

  const onSubmit = useCallback(
    (data: PrimaryDetailFormType) => {
      const payload = buildPayload(data);
      console.log('payload', payload, 'brokerId', brokerId);
      postPurchaseRequest(payload)
        .unwrap()
        .then((res) => {
          displaySuccess(res?.message);
          navigate(
            `${purchaseFormRoutes['income_detail']}/${res.data.application_id}`,
          );
        })
        .catch((err) => {
          displayError(err);
        });
    },
    [getValues, id, postPurchaseRequest, brokerId],
  );

  const handleFormSubmit = () => {
    if (submitButtonRef.current) {
      submitButtonRef.current.click();
    }
  };

  const formRef = useRef<HTMLFormElement>(null);

  const saveAsDraft = useCallback(async () => {
    const rawData = getValues();
    const initialData = primaryDetailSchemaWithOptionalFields.cast(rawData, {
      stripUnknown: true,
    });

    console.log('initialData', initialData);

    const data = Object.fromEntries(
      Object.entries(initialData).filter(([_, value]) => value != null),
    );

    console.log('data', data);

    const payload = {
      data,
      draft: true,
      step: 'primary_detail' as const,
      ...(id ? { application_id: id } : {}),
      ...(brokerId ? { broker_id: brokerId } : {}),
    };
    try {
      const res = await postPurchaseRequest(payload as any);
      console.log('response', res);
      message.success(res?.data?.message);
    } catch (error) {
      message.error('Error while saving as draft');
    }
  }, [getValues, id, postPurchaseRequest, brokerId]);

  return (
    <>
      <Row>
        <Col xs={24}>
          <StepperFormLayout
            showBackButton={false}
            handleSubmit={handleFormSubmit}
            nextBtnText="Next: Income Details"
            isSubmitLoading={postPurchaseRequestLoading}
            showDraftButton={true}
            handleSaveAsDraftClick={saveAsDraft}
            isDraftLoading={postPurchaseRequestLoading}
          >
            <Row
              style={{
                paddingBottom: '20px',
              }}
              gutter={[0, 4]}
            >
              <Col xs={24}>
                <StepProgress
                  completedStep={0}
                  totalSteps={4}
                  formId="purchase_detail"
                  stepData={stepData}
                  id={id || ''}
                />
              </Col>
            </Row>
            <Container width="md">
              <Row gutter={[24, 0]}>
                <Col xs={24} md={24}>
                  <Card title="Primary Details">
                    <Col xs={24}>
                      <form
                        autoComplete="off"
                        ref={formRef}
                        onSubmit={handleSubmit(onSubmit)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                          }
                        }}
                      >
                        <NewPurchaseDetail
                          data={yourPurchaseDetailData}
                          setValue={setValue}
                          watch={watch}
                          control={control}
                          errors={errors}
                          register={register}
                          unregister={unregister}
                          resetField={resetField}
                        />
                        <NewPersonalDetail
                          data={yourPurchaseDetailData}
                          setValue={setValue}
                          watch={watch}
                          control={control}
                          errors={errors}
                          unregister={unregister}
                          getValues={getValues}
                          resetField={resetField}
                        />
                        <NewPropertyDetail
                          data={yourPurchaseDetailData}
                          setValue={setValue}
                          watch={watch}
                          control={control}
                          errors={errors}
                          unregister={unregister}
                          resetField={resetField}
                        />
                        <Col xs={24}>
                          <Button
                            ref={submitButtonRef}
                            type="primary"
                            htmlType="submit"
                            style={{ display: 'none' }}
                          >
                            Submit
                          </Button>
                        </Col>
                      </form>
                    </Col>
                  </Card>
                </Col>
              </Row>
            </Container>
          </StepperFormLayout>
        </Col>
        {/* <Col xl={6}>
          <PurchaseRightSidebar />
        </Col> */}
      </Row>
    </>
  );
};

export default YourPurchaseDetail;
